import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';

const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: ${props => props.theme === 'dark' ? '#1e1e1e' : '#f8f9fa'};
`;

const FeaturesContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 30px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled(motion.div)`
  background-color: ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.10);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background-color: ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: ${props => props.theme === 'dark' ? '#fff' : '#23272f'};
  font-size: 1.5rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  border: 1.5px solid ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  transition: background 0.3s, color 0.3s;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 15px;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  line-height: 1.6;
`;

const Features = ({ theme }) => {
  const { language } = useLanguage();
  const t = translations.features[language];
  
  const featuresData = [
    {
      icon: '🔍',
      title: t.transparentDecision.title,
      description: t.transparentDecision.description
    },
    {
      icon: '📊',
      title: t.comprehensiveAnalytics.title,
      description: t.comprehensiveAnalytics.description
    },
    {
      icon: '🛡️',
      title: t.biasDetection.title,
      description: t.biasDetection.description
    },
    {
      icon: '🔄',
      title: t.continuousLearning.title,
      description: t.continuousLearning.description
    },
    {
      icon: '👥',
      title: t.humanAICollaboration.title,
      description: t.humanAICollaboration.description
    },
    {
      icon: '🔒',
      title: t.privacyFocused.title,
      description: t.privacyFocused.description
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <FeaturesSection theme={theme} id="features">
      <FeaturesContainer>
        <SectionHeader>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <SectionTitle theme={theme}>{t.title}</SectionTitle>
            <SectionSubtitle theme={theme}>
              {t.subtitle}
            </SectionSubtitle>
          </motion.div>
        </SectionHeader>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <FeaturesGrid>
            {featuresData.map((feature, index) => (
              <FeatureCard 
                key={index}
                theme={theme}
                variants={itemVariants}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle theme={theme}>{feature.title}</FeatureTitle>
                <FeatureDescription theme={theme}>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </motion.div>
      </FeaturesContainer>
    </FeaturesSection>
  );
};

export default Features;
